import { useState, useMemo } from "react"
import {Landmark, ChevronDown, RotateCcw, TrendingUp, AlertTriangle, CheckCircle, Leaf, Droplets, Ruler, Weight, CalendarDays, Info} from 'lucide-react'

interface ForageType {
  name: string
  aum_per_acre: number
  description: string
}

const FORAGE_TYPES: ForageType[] = [
  { name: "Excellent Native Grass", aum_per_acre: 1.2, description: "Lush, well-managed native pasture with high forage production" },
  { name: "Good Improved Pasture", aum_per_acre: 1.0, description: "Improved grass varieties (bermuda, fescue) in good condition" },
  { name: "Average Mixed Pasture", aum_per_acre: 0.7, description: "Mixed grass and forbs with moderate productivity" },
  { name: "Fair Rangeland", aum_per_acre: 0.45, description: "Semi-arid rangeland with moderate forage availability" },
  { name: "Poor / Overgrazed Land", aum_per_acre: 0.25, description: "Degraded pasture needing recovery and management" },
  { name: "Irrigated Pasture", aum_per_acre: 1.6, description: "Irrigated improved pasture with high carrying capacity" },
]

interface RainfallZone {
  label: string
  inches: string
  multiplier: number
}

const RAINFALL_ZONES: RainfallZone[] = [
  { label: "Arid (< 15 in/yr)", inches: "<15", multiplier: 0.6 },
  { label: "Semi-Arid (15-25 in/yr)", inches: "15-25", multiplier: 0.8 },
  { label: "Moderate (25-35 in/yr)", inches: "25-35", multiplier: 1.0 },
  { label: "Adequate (35-45 in/yr)", inches: "35-45", multiplier: 1.15 },
  { label: "Abundant (> 45 in/yr)", inches: ">45", multiplier: 1.3 },
]

function getRiskLevel(acresPerHead: number): { label: string; color: string; bgColor: string; icon: "check" | "warning" | "alert"; message: string } {
  if (acresPerHead >= 8) {
    return { label: "Very Low Risk", color: "#2D6A4F", bgColor: "#B7E4C7", icon: "check", message: "Excellent stocking density. Pasture has ample recovery time and forage reserves." }
  }
  if (acresPerHead >= 4) {
    return { label: "Low Risk", color: "#40916C", bgColor: "#d1f0dd", icon: "check", message: "Good stocking rate. Pasture should sustain cattle well with proper rotation." }
  }
  if (acresPerHead >= 2) {
    return { label: "Moderate Risk", color: "#D4A373", bgColor: "#FEFAE0", icon: "warning", message: "Monitor closely. Consider rotational grazing to prevent forage depletion." }
  }
  if (acresPerHead >= 1) {
    return { label: "High Risk", color: "#E76F51", bgColor: "#fce8e2", icon: "alert", message: "High stocking density. Overgrazing is likely without supplemental feeding." }
  }
  return { label: "Overstocked", color: "#c0392b", bgColor: "#fde2e2", icon: "alert", message: "Critically overstocked. Immediate action needed to prevent pasture degradation." }
}

export default function CattlePerAcreCalculator() {
  const [totalAcres, setTotalAcres] = useState("")
  const [forageType, setForageType] = useState(FORAGE_TYPES[2]) // Default: Average Mixed Pasture
  const [rainfallZone, setRainfallZone] = useState(RAINFALL_ZONES[2]) // Default: Moderate
  const [cattleWeight, setCattleWeight] = useState("1100")
  const [grazingMonths, setGrazingMonths] = useState("12")
  const [showResults, setShowResults] = useState(false)

  const results = useMemo(() => {
    const acres = parseFloat(totalAcres)
    const weight = parseFloat(cattleWeight)
    const months = parseFloat(grazingMonths)

    if (!acres || acres <= 0 || !weight || weight <= 0 || !months || months <= 0) return null

    // 1 Animal Unit (AU) = 1,000 lb cow with calf consuming ~26 lbs dry matter/day
    const animalUnitEquivalent = weight / 1000

    // Total AUMs the land can provide
    const baseAUM = forageType.aum_per_acre * acres * rainfallZone.multiplier

    // Recommended head count based on grazing season
    const recommendedHead = Math.floor(baseAUM / (animalUnitEquivalent * months))

    // Acres per head
    const acresPerHead = recommendedHead > 0 ? acres / recommendedHead : acres

    // Stocking rate (AU per acre)
    const stockingRate = recommendedHead > 0 ? (recommendedHead * animalUnitEquivalent) / acres : 0

    // Daily forage demand (lbs) — a 1,000 lb cow eats ~26 lbs dry matter/day
    const dailyForageDemand = recommendedHead * (weight * 0.026)

    // Monthly forage demand (tons)
    const monthlyForageTons = (dailyForageDemand * 30.4) / 2000

    const risk = getRiskLevel(acresPerHead)

    return {
      recommendedHead: Math.max(recommendedHead, 0),
      totalAUM: baseAUM,
      acresPerHead,
      stockingRate,
      dailyForageDemand,
      monthlyForageTons,
      risk,
      animalUnitEquivalent,
    }
  }, [totalAcres, forageType, rainfallZone, cattleWeight, grazingMonths])

  const handleCalculate = () => {
    if (totalAcres && parseFloat(totalAcres) > 0) {
      setShowResults(true)
    }
  }

  const handleReset = () => {
    setTotalAcres("")
    setCattleWeight("1100")
    setGrazingMonths("12")
    setForageType(FORAGE_TYPES[2])
    setRainfallZone(RAINFALL_ZONES[2])
    setShowResults(false)
  }

  return (
    <section id="cattle-per-acre-calculator" className="py-12 sm:py-16 lg:py-20 bg-[#FEFAE0]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-8 sm:mb-12">
          <span className="inline-block text-[#40916C] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3">
            Stocking Rate Calculator
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#283618]">
            How Many Cattle Per Acre?
          </h2>
          <p className="mt-3 text-[#606C38] text-base sm:text-lg max-w-xl mx-auto">
            Enter your pasture details to calculate the optimal number of cattle your land can sustainably support without overgrazing.
          </p>
        </div>

        {/* Calculator Card */}
        <div className="bg-white rounded-2xl shadow-xl shadow-[#2D6A4F]/8 border border-[#B7E4C7]/40 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#2D6A4F] to-[#40916C] px-6 sm:px-8 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Landmark className="w-5 h-5 text-[#FEFAE0]" />
              </div>
              <div>
                <h3 className="text-[#FEFAE0] font-bold text-lg" style={{ fontFamily: "'Merriweather', serif" }}>
                  Cattle Per Acre Calculator
                </h3>
                <p className="text-[#B7E4C7] text-sm">
                  Pasture capacity &amp; stocking rate estimation
                </p>
              </div>
            </div>
          </div>

          {/* Input Section */}
          <div className="p-6 sm:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {/* Total Acres */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                  <Ruler className="w-3.5 h-3.5 text-[#40916C]" />
                  Total Pasture Acres
                </label>
                <input
                  type="number"
                  value={totalAcres}
                  onChange={(e) => {
                    setTotalAcres(e.target.value)
                    setShowResults(false)
                  }}
                  placeholder="e.g. 160"
                  min="1"
                  className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all placeholder:text-[#606C38]/40"
                />
                <p className="mt-1.5 text-xs text-[#606C38]">
                  Total usable grazing acreage
                </p>
              </div>

              {/* Forage Type */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                  <Leaf className="w-3.5 h-3.5 text-[#40916C]" />
                  Forage / Pasture Quality
                </label>
                <div className="relative">
                  <select
                    value={forageType.name}
                    onChange={(e) => {
                      const ft = FORAGE_TYPES.find((f) => f.name === e.target.value)
                      if (ft) {
                        setForageType(ft)
                        setShowResults(false)
                      }
                    }}
                    className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all appearance-none cursor-pointer pr-10"
                  >
                    {FORAGE_TYPES.map((ft) => (
                      <option key={ft.name} value={ft.name}>
                        {ft.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#606C38] pointer-events-none" />
                </div>
                <p className="mt-1.5 text-xs text-[#606C38]">
                  {forageType.description}
                </p>
              </div>

              {/* Rainfall Zone */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                  <Droplets className="w-3.5 h-3.5 text-[#40916C]" />
                  Annual Rainfall
                </label>
                <div className="relative">
                  <select
                    value={rainfallZone.label}
                    onChange={(e) => {
                      const rz = RAINFALL_ZONES.find((r) => r.label === e.target.value)
                      if (rz) {
                        setRainfallZone(rz)
                        setShowResults(false)
                      }
                    }}
                    className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all appearance-none cursor-pointer pr-10"
                  >
                    {RAINFALL_ZONES.map((rz) => (
                      <option key={rz.label} value={rz.label}>
                        {rz.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#606C38] pointer-events-none" />
                </div>
                <p className="mt-1.5 text-xs text-[#606C38]">
                  Rainfall affects forage growth and carrying capacity
                </p>
              </div>

              {/* Cattle Weight */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                  <Weight className="w-3.5 h-3.5 text-[#40916C]" />
                  Average Cattle Weight (lbs)
                </label>
                <input
                  type="number"
                  value={cattleWeight}
                  onChange={(e) => {
                    setCattleWeight(e.target.value)
                    setShowResults(false)
                  }}
                  placeholder="e.g. 1100"
                  min="400"
                  max="2500"
                  className="w-full px-4 py-3.5 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] text-base font-medium focus:border-[#2D6A4F] focus:ring-2 focus:ring-[#2D6A4F]/20 outline-none transition-all placeholder:text-[#606C38]/40"
                />
                <p className="mt-1.5 text-xs text-[#606C38]">
                  1 Animal Unit = 1,000 lb cow with calf
                </p>
              </div>

              {/* Grazing Season */}
              <div className="sm:col-span-2">
                <label className="flex items-center gap-1.5 text-sm font-semibold text-[#283618] mb-2">
                  <CalendarDays className="w-3.5 h-3.5 text-[#40916C]" />
                  Grazing Season Length (months)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="1"
                    max="12"
                    step="1"
                    value={grazingMonths}
                    onChange={(e) => {
                      setGrazingMonths(e.target.value)
                      setShowResults(false)
                    }}
                    className="flex-1 h-2 bg-[#B7E4C7] rounded-full appearance-none cursor-pointer accent-[#2D6A4F]"
                  />
                  <div className="w-20 text-center px-3 py-2 bg-[#FEFAE0] border-2 border-[#B7E4C7] rounded-xl text-[#283618] font-bold text-base">
                    {grazingMonths} mo
                  </div>
                </div>
                <p className="mt-1.5 text-xs text-[#606C38]">
                  {parseInt(grazingMonths) === 12 ? "Year-round grazing" : `${grazingMonths} months of active grazing per year`}
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleCalculate}
                disabled={!totalAcres || parseFloat(totalAcres) <= 0}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[#2D6A4F] hover:bg-[#245a42] disabled:bg-[#B7E4C7] disabled:cursor-not-allowed text-[#FEFAE0] font-bold text-base sm:text-lg px-6 py-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:shadow-none disabled:translate-y-0"
              >
                <TrendingUp className="w-5 h-5" />
                Calculate Stocking Rate
              </button>
              {showResults && (
                <button
                  onClick={handleReset}
                  className="inline-flex items-center justify-center gap-2 bg-[#FEFAE0] hover:bg-[#f0ead2] text-[#606C38] font-semibold px-5 py-4 rounded-xl border-2 border-[#B7E4C7] transition-all duration-200"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Results Section */}
          {showResults && results && (
            <div className="border-t-2 border-[#B7E4C7]/40 bg-gradient-to-b from-[#FEFAE0]/50 to-white p-6 sm:p-8 animate-fade-in-up">
              {/* Main Result */}
              <div className="text-center mb-6 pb-6 border-b border-[#B7E4C7]/30">
                <p className="text-sm font-semibold text-[#40916C] uppercase tracking-wider mb-1">
                  Recommended Stocking Rate
                </p>
                <p className="text-4xl sm:text-5xl font-bold text-[#283618]" style={{ fontFamily: "'Merriweather', serif" }}>
                  {results.recommendedHead}
                  <span className="text-lg sm:text-xl font-semibold text-[#606C38] ml-2">head</span>
                </p>
                <p className="mt-2 text-sm text-[#606C38]">
                  on {totalAcres} acres for {grazingMonths} month{parseInt(grazingMonths) !== 1 ? "s" : ""} of grazing
                </p>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                {/* Acres Per Head */}
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Ruler className="w-4 h-4 text-[#2D6A4F]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Acres / Head</span>
                  </div>
                  <p className="text-3xl font-bold text-[#283618]">
                    {results.recommendedHead > 0 ? results.acresPerHead.toFixed(1) : "N/A"}
                  </p>
                  <p className="text-xs text-[#606C38] mt-1">acres per animal</p>
                </div>

                {/* Total AUMs */}
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <Leaf className="w-4 h-4 text-[#40916C]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">Total AUMs</span>
                  </div>
                  <p className="text-3xl font-bold text-[#283618]">{results.totalAUM.toFixed(1)}</p>
                  <p className="text-xs text-[#606C38] mt-1">Animal Unit Months available</p>
                </div>

                {/* Stocking Rate */}
                <div className="bg-white rounded-xl p-4 border border-[#B7E4C7]/30 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-[#D4A373]" />
                    <span className="text-xs font-semibold text-[#606C38] uppercase tracking-wider">AU / Acre</span>
                  </div>
                  <p className="text-3xl font-bold text-[#283618]">{results.stockingRate.toFixed(2)}</p>
                  <p className="text-xs text-[#606C38] mt-1">animal units per acre</p>
                </div>
              </div>

              {/* Forage Demand Summary */}
              <div className="bg-[#FEFAE0] rounded-xl p-4 sm:p-5 border border-[#B7E4C7]/30 mb-6">
                <h4 className="font-bold text-sm text-[#283618] mb-3 flex items-center gap-2">
                  <Info className="w-4 h-4 text-[#40916C]" />
                  Forage Demand Breakdown
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <span className="text-[#606C38]">AU Equivalent:</span>
                    <span className="ml-1 font-semibold text-[#283618]">{results.animalUnitEquivalent.toFixed(2)} AU per head</span>
                  </div>
                  <div>
                    <span className="text-[#606C38]">Daily Forage:</span>
                    <span className="ml-1 font-semibold text-[#283618]">{results.dailyForageDemand.toFixed(0)} lbs/day</span>
                  </div>
                  <div>
                    <span className="text-[#606C38]">Monthly Forage:</span>
                    <span className="ml-1 font-semibold text-[#283618]">{results.monthlyForageTons.toFixed(1)} tons/month</span>
                  </div>
                </div>
              </div>

              {/* Risk Assessment */}
              <div
                className="rounded-xl p-4 sm:p-5 flex flex-col sm:flex-row items-start gap-3 sm:gap-4 border"
                style={{ backgroundColor: results.risk.bgColor, borderColor: results.risk.color + "30" }}
              >
                <div className="shrink-0 mt-0.5">
                  {results.risk.icon === "check" ? (
                    <CheckCircle className="w-5 h-5" style={{ color: results.risk.color }} />
                  ) : results.risk.icon === "warning" ? (
                    <AlertTriangle className="w-5 h-5" style={{ color: results.risk.color }} />
                  ) : (
                    <AlertTriangle className="w-5 h-5" style={{ color: results.risk.color }} />
                  )}
                </div>
                <div>
                  <p className="font-bold text-sm" style={{ color: results.risk.color }}>
                    Overgrazing Risk: {results.risk.label}
                  </p>
                  <p className="text-sm text-[#283618]/80 mt-1 leading-relaxed">
                    {results.risk.message}
                  </p>
                </div>
              </div>

              {/* Disclaimer */}
              <p className="mt-5 text-xs text-[#606C38]/70 text-center leading-relaxed">
                These estimates are based on general USDA guidelines and may vary with local conditions, soil type, and management practices. Consult your local extension office for site-specific recommendations.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
